import React, { useEffect } from "react"
import styled from "styled-components"
import { Link } from "gatsby"
import { gsap } from "gsap"
import { DrawSVGPlugin } from "gsap/DrawSVGPlugin"

const Pagination = ({ pageContext }) => {
  const { currentPage, numPages, slug } = pageContext
  const isFirst = currentPage === 1
  const isLast = currentPage === numPages
  const prevPage =
    currentPage - 1 === 1 ? "/" : `/${(currentPage - 1).toString()}`
  const nextPage = (currentPage + 1).toString()

  useEffect(() => {
    if (typeof window !== `undefined`) {
      gsap.registerPlugin(DrawSVGPlugin)
    }
    gsap.set(".circle", {
      drawSVG: "0% 0%",
      transformOrigin: "50% 50%",
      rotation: -90,
    })
  }, [])

  const hoverOn = e => {
    const circle = e.target.querySelector(".circle")
    gsap.fromTo(
      circle,
      { drawSVG: "0% 0%", duration: 0.5 },
      { drawSVG: "0% 100%", duration: 0.5 }
    )
  }

  const hoverOff = e => {
    const circle = e.target.querySelector(".circle")
    gsap.fromTo(
      circle,
      { drawSVG: "0% 100%", duration: 0.5 },
      { drawSVG: "100% 100%", duration: 0.5 }
    )
  }

  return (
    <Nav>
      {!isFirst && (
        <PrevButton
          to={`/${slug}${prevPage}`}
          rel="prev"
          onMouseEnter={hoverOn}
          onMouseLeave={hoverOff}
          aria-label="Previous Page"
        >
          <Circle viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="40" fill="none" className="circle" />
          </Circle>
          <Arrow
            xmlns="http://www.w3.org/2000/svg"
            width="28"
            height="28"
            fill="none"
            viewBox="0 0 28 28"
          >
            <path
              fill="#22313E"
              d="M16.24 22.4l1.96-2.1-5.88-6.3 5.88-6.3-1.96-2.1L8.4 14l7.84 8.4z"
            ></path>
          </Arrow>
        </PrevButton>
      )}
      <Numbers>
        {Array.from({ length: numPages }, (_, i) => (
          <Number key={`pagination-number${i + 1}`}>
            <Link
              to={`/${slug}/${i === 0 ? "" : i + 1}`}
              activeClassName="-active"
            >
              {i + 1}
            </Link>
          </Number>
        ))}
      </Numbers>
      {!isLast && (
        <NextButton
          to={`/${slug}/${nextPage}`}
          rel="next"
          onMouseEnter={hoverOn}
          onMouseLeave={hoverOff}
          aria-label="Next Page"
        >
          <Circle viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="40" fill="none" className="circle" />
          </Circle>
          <Arrow
            xmlns="http://www.w3.org/2000/svg"
            width="28"
            height="28"
            fill="none"
            viewBox="0 0 28 28"
          >
            <path
              fill="#22313E"
              d="M11.76 5.6L9.8 7.7l5.88 6.3-5.88 6.3 1.96 2.1L19.6 14l-7.84-8.4z"
            ></path>
          </Arrow>
        </NextButton>
      )}
    </Nav>
  )
}

export default Pagination

const Nav = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  width: 100%;
`

const Circle = styled.svg`
  height: 3rem;
  left: -0.25rem;
  position: absolute;
  top: -0.25rem;
  width: 3rem;
  stroke: var(--colorPrimary);
  stroke-width: 3px;
`

const Arrow = styled.svg`
  path {
    fill: var(--gray--60);
  }
`

const PageButton = styled(Link)`
  align-items: center;
  border-radius: 2rem;
  background-color: var(--gray--40);
  display: flex;
  height: 2.5rem;
  justify-content: center;
  margin: 0 1rem;
  overflow: hidden;
  position: relative;
  transition: var(--transitionBase);
  width: 2.5rem;

  &:hover {
    background-color: transparent;

    ${Arrow} {
      path {
        fill: var(--colorPrimary);
      }
    }
  }
`

const PrevButton = styled(PageButton)``

const NextButton = styled(PageButton)``

const Numbers = styled.ul`
  display: flex;
`

const Number = styled.li`
  a {
    color: var(--gray--50);
    display: block;
    font-size: 1.25rem;
    padding: 0 0.5rem;
    position: relative;
    text-decoration: none;
    transition: var(--transitionBase);

    &:after {
      background-color: var(--gray--50);
      bottom: -0.15rem;
      content: "";
      height: 3px;
      left: 0;
      margin: 0 auto;
      opacity: 0;
      position: absolute;
      right: 0;
      transform: translateY(-0.15rem);
      transition: var(--transitionBase);
      width: 50%;
    }

    :hover {
      :after {
        opacity: 1;
        transform: translateY(0rem);
      }
    }

    &.-active {
      color: var(--gray--80);

      &:after {
        background-color: var(--gray--80);
        opacity: 1;
        transform: translateY(0rem);
      }
    }
  }
`
